// Filteren en sorteren van de lijst, en de keuze bewaren in de hash-query (#/?cat=…&toon=…&sort=…).
// Puur: geen DOM, zodat het in node getest kan worden.
import type { GuestGift } from '../shared/api-types.ts';

export type Show = 'alle' | 'beschikbaar' | 'geschonken';
export type Sort = 'aanbevolen' | 'prijs' | 'prijs-af' | 'titel';
export type ListQuery = { cat: string | null; show: Show; sort: Sort };

export const DEFAULT_QUERY: ListQuery = { cat: null, show: 'alle', sort: 'aanbevolen' };

const SHOWS: readonly Show[] = ['alle', 'beschikbaar', 'geschonken'];
const SORTS: readonly Sort[] = ['aanbevolen', 'prijs', 'prijs-af', 'titel'];
const CAT_MAX = 40;
const collator = new Intl.Collator('nl', { sensitivity: 'base', numeric: true });

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return allowed.find((a) => a === value) ?? fallback;
}

export function parseListQuery(params: URLSearchParams): ListQuery {
  const cat = (params.get('cat') ?? '').trim().slice(0, CAT_MAX);
  return {
    cat: cat || null,
    show: oneOf(params.get('toon'), SHOWS, DEFAULT_QUERY.show),
    sort: oneOf(params.get('sort'), SORTS, DEFAULT_QUERY.sort),
  };
}

/** "" bij de standaardkeuze, anders "?cat=…&toon=…&sort=…" (alleen wat afwijkt). */
export function listQueryToString(q: ListQuery): string {
  const params = new URLSearchParams();
  if (q.cat) params.set('cat', q.cat);
  if (q.show !== DEFAULT_QUERY.show) params.set('toon', q.show);
  if (q.sort !== DEFAULT_QUERY.sort) params.set('sort', q.sort);
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function isDefaultQuery(q: ListQuery): boolean {
  return listQueryToString(q) === '';
}

/** De categorieën die op de lijst voorkomen, uniek en alfabetisch. */
export function categoriesOf(gifts: GuestGift[]): string[] {
  const set = new Set<string>();
  for (const g of gifts) if (g.category) set.add(g.category);
  return [...set].sort(collator.compare);
}

/** Een categorie die niet (meer) op de lijst staat, telt als "Alle". */
export function withKnownCategory(q: ListQuery, categories: string[]): ListQuery {
  return q.cat && !categories.includes(q.cat) ? { ...q, cat: null } : q;
}

/** Een gekozen cadeau, of een potje waarvan het doel bereikt is. */
export function isGiven(g: GuestGift): boolean {
  if (g.kind === 'cadeau') return g.taken;
  return g.priceCents > 0 && g.remainingCents !== null && g.remainingCents <= 0;
}

function priceOf(g: GuestGift): number {
  return g.kind === 'potje' && g.remainingCents !== null ? g.remainingCents : g.priceCents;
}

function compareFor(sort: Sort): (a: GuestGift, b: GuestGift) => number {
  if (sort === 'prijs') return (a, b) => priceOf(a) - priceOf(b);
  if (sort === 'prijs-af') return (a, b) => priceOf(b) - priceOf(a);
  if (sort === 'titel') return (a, b) => collator.compare(a.title, b.title);
  return (a, b) => Number(b.favorite) - Number(a.favorite); // stabiel: daarna de volgorde van de ouders
}

/** Filtert en sorteert; de volgorde van de server (sortOrder) blijft de tiebreaker. */
export function applyQuery(gifts: GuestGift[], q: ListQuery): GuestGift[] {
  return gifts
    .filter((g) => q.cat === null || g.category === q.cat)
    .filter((g) => q.show === 'alle' || (q.show === 'geschonken') === isGiven(g))
    .sort(compareFor(q.sort));
}
